import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import MainLayout from './MainLayout';
import Track from '../pages/Track';
import History from '../pages/History';
import Statistics from '../pages/Statistics';
import Predictions from '../pages/Predictions';
import Preferences from '../pages/Preferences';
import Settings from '../pages/Settings';
import Feedback from '../pages/Feedback';
import Support from '../pages/Support';
import UserOnboarding from './UserOnboarding';
import { startTracking, identifyUser } from '../utils/openreplay';

interface LoggedInAppProps {
  user: any;
  onLogout: () => void;
  onUserUpdate: (userData: any) => void;
}

const LoggedInApp: React.FC<LoggedInAppProps> = ({ user, onLogout, onUserUpdate }) => {
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);

  const checkUserStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const babies = await response.json();
        setNeedsOnboarding(babies.length === 0);
      } else {
        setNeedsOnboarding(true);
      }
    } catch (error) {
      console.error('Failed to check user status:', error);
      setNeedsOnboarding(true);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  // Identify authenticated users in OpenReplay
  // Note: Tracking is already started in App.tsx for all users
  useEffect(() => {
    console.log('[OpenReplay Debug] LoggedInApp useEffect triggered:', {
      hasUser: !!user,
      userId: user?.id,
      needsOnboarding,
      shouldIdentifyUser: !!(user && !needsOnboarding)
    });

    if (user && !needsOnboarding) {
      console.log('[OpenReplay Debug] Identifying user:', user.id);

      // Ensure tracking is started (safe to call multiple times)
      startTracking();

      // Identify user with metadata
      identifyUser(user.id, {
        username: user.username,
        email: user.email,
        has_active_baby: !!user.active_baby_id,
        time_format_24h: user.time_format_24h,
        created_at: user.created_at,
      });
    } else {
      console.log('[OpenReplay Debug] Not identifying user - conditions not met');
    }
  }, [user, needsOnboarding]);

  const handleOnboardingComplete = async () => {
    // Refresh user data and check status again
    try {
      const userResponse = await fetch(`${API_BASE_URL}/api/users/${user.id}`);
      if (userResponse.ok) {
        const updatedUser = await userResponse.json();
        onUserUpdate(updatedUser);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
    
    await checkUserStatus();
  };

  if (loading) {
    return (
      <div className="app-loading">
        <h2>Loading...</h2>
        <p>Setting up your account...</p>
      </div>
    );
  }

  if (needsOnboarding) {
    return <UserOnboarding user={user} onComplete={handleOnboardingComplete} />;
  }

  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        <Route index element={<Navigate to="/track" replace />} />
        <Route path="track" element={<Track user={user} />} />
        <Route path="history" element={<History user={user} />} />
        <Route path="statistics" element={<Statistics user={user} />} />
        <Route path="predictions" element={<Predictions user={user} />} />
        <Route path="preferences" element={<Preferences user={user} />} />
        <Route path="feedback" element={<Feedback user={user} />} />
        <Route path="support" element={<Support user={user} />} />
        <Route path="settings" element={<Settings user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />} />
      </Route>
    </Routes>
  );
};

export default LoggedInApp;